import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart2, Palette, MapPin, Clock, Music, HelpCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import useIsMobile from '../hooks/useIsMobile';
import PageLoader from '../components/PageLoader';
import { useCanEdit } from '../context/BandContext';
import OfflineBanner from '../components/OfflineBanner';
import { cacheData, loadWithSavedCopy } from '../utils/offlineCache';
import { saveUpcomingSetLists } from '../utils/saveUpcomingSetLists';

const API_URL = process.env.REACT_APP_API_URL || '';

function Dashboard() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const canEdit = useCanEdit();
  const [activeGig, setActiveGig] = useState(null);
  const [gigs, setGigs] = useState([]);
  const [stats, setStats] = useState({
    totalSongs: 0,
    totalGigs: 0,
    pendingRequests: 0
  });
  const [loading, setLoading] = useState(true);
  const [offlineSince, setOfflineSince] = useState(null);
  const [calModal, setCalModal] = useState(null); // { day, gigsOnDay: [gig, ...] }

  useEffect(() => {
    fetchStats({ emptyPage: true });
  }, []);

  const fetchStats = async (opts) => {
    try {
      const cachedAt = await loadWithSavedCopy('dashboardHome', async () => {
        const [songsRes, gigsRes, activeRes] = await Promise.all([
          axios.get(`${API_URL}/api/songs`),
          axios.get(`${API_URL}/api/gigs`),
          axios.get(`${API_URL}/api/gigs/active`)
        ]);
        const active = activeRes.data;
        // Pending requests only exist for the active gig
        const requestsRes = active
          ? await axios.get(`${API_URL}/api/requests/gig/${encodeURIComponent(active.unique_url)}`)
          : { data: [] };

        const home = {
          activeGig: active,
          gigs: gigsRes.data,
          stats: {
            totalSongs: songsRes.data.filter(s => s.is_active).length,
            totalGigs: gigsRes.data.length,
            pendingRequests: requestsRes.data.length
          },
        };
        cacheData('gigs', home.gigs);  // so the Gigs page opens offline too
        saveUpcomingSetLists(home.gigs);
        return home;
      }, home => {
        setActiveGig(home.activeGig);
        setGigs(home.gigs);
        setStats(home.stats);
        setLoading(false);
      }, opts);
      setOfflineSince(cachedAt);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setLoading(false);
    }
  };

  if (loading) return <PageLoader />;

  // ── Calendar helpers ──────────────────────────────────────────────────────
  const now = new Date();
  const calYear = now.getFullYear();
  const calMonth = now.getMonth();
  const calToday = now.getDate();

  const firstOfMonth = new Date(calYear, calMonth, 1);
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  // UK Monday-first: convert Sun=0…Sat=6 → Mon=0…Sun=6
  const startOffset = (firstOfMonth.getDay() + 6) % 7;

  // Map day-of-month → array of full gig objects for current month
  const gigDateMap = {};
  gigs.forEach(gig => {
    if (!gig.gig_date) return;
    const [y, m, d] = gig.gig_date.split('T')[0].split('-').map(Number);
    if (y === calYear && m - 1 === calMonth) {
      if (!gigDateMap[d]) gigDateMap[d] = [];
      gigDateMap[d].push(gig);
    }
  });

  const monthLabel = firstOfMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  // Calendar cells: leading nulls + day numbers
  const calCells = [];
  for (let i = 0; i < startOffset; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);

  const sortedGigDays = Object.keys(gigDateMap).map(Number).sort((a, b) => a - b);
  // ─────────────────────────────────────────────────────────────────────────

  const navCards = [
    { href: '/dashboard/analytics', Icon: BarChart2,  title: 'Analytics',     desc: 'View insights and trends from your requests' },
    { href: '/dashboard/branding',  Icon: Palette,    title: 'Branding & Settings', desc: 'Customise your audience page theme and social links' },
    { href: '/dashboard/help',      Icon: HelpCircle, title: 'Help & Guides', desc: 'How-to guides for getting the most out of GigRequest' },
  ];

  return (
    <div className="gr-fade" style={{ background: theme.background }}>
    <OfflineBanner cachedAt={offlineSince} label="gigs" onSignalBack={fetchStats} />
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '1rem' : '2rem', color: theme.text }}>

      {/* Active Gig Card — full-width banner above the 2-col grid */}
      {activeGig && (
        <div style={{
          background: theme.navGradient,
          borderRadius: '12px',
          padding: '0.85rem 1.1rem',
          marginBottom: '1rem',
          color: 'white',
          boxShadow: `0 4px 16px rgba(0,0,0,0.25)`,
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          gap: '0.75rem',
        }}>
          <div>
            <div style={{ fontSize: '0.68rem', opacity: 0.8, marginBottom: '0.2rem', letterSpacing: '0.08em', fontWeight: '600' }}>
              ACTIVE GIG
            </div>
            <div style={{ fontWeight: '700', fontSize: '1rem', lineHeight: 1.3 }}>
              {activeGig.gig_name}
            </div>
            <div style={{ fontSize: '0.82rem', opacity: 0.9, marginTop: '0.1rem' }}>
              {activeGig.venue_name}{activeGig.venue_location ? ` · ${activeGig.venue_location}` : ''}
            </div>
          </div>
          <a
            href="/dashboard/requests"
            style={{
              display: 'block',
              padding: '0.55rem 0.8rem',
              background: 'rgba(255,255,255,0.15)',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontWeight: '700',
              fontSize: '0.8rem',
              textAlign: 'center',
              width: '72px',
              lineHeight: 1.3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          >
            Go to Live Requests
          </a>
        </div>
      )}

      {/* Desktop 2-col layout: calendar left, stats + nav right */}
      <div style={{
        display: isMobile ? 'block' : 'grid',
        gridTemplateColumns: '360px 1fr',
        gap: '1.5rem',
        alignItems: 'start',
      }}>

      {/* LEFT: Monthly Calendar */}
      <div style={{
        background: theme.surface,
        borderRadius: '12px',
        border: `0.5px solid ${theme.border}`,
        padding: isMobile ? '0.85rem 0.85rem 0.75rem' : '1.1rem 1.25rem 1rem',
        marginBottom: isMobile ? '1rem' : 0,
        boxShadow: theme.isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        {/* Month heading */}
        <div style={{ textAlign: 'center', fontSize: '0.78rem', fontWeight: '700', color: theme.textSecondary, marginBottom: '0.55rem', letterSpacing: '0.06em' }}>
          {monthLabel.toUpperCase()}
        </div>

        {/* Day-of-week headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '0.15rem' }}>
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: '0.62rem', color: theme.textMuted, fontWeight: '600', padding: '0.1rem 0' }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {calCells.map((day, i) => {
            const gigsOnDay = day !== null ? gigDateMap[day] : null;
            const hasGig = Boolean(gigsOnDay);
            const isToday = day === calToday;
            const isPast = day !== null && day < calToday;

            // Circle appearance
            let bg = 'transparent';
            let color = day ? theme.text : 'transparent';
            let fontWeight = '400';
            let border = 'none';
            let cursor = 'default';

            if (hasGig) {
              cursor = 'pointer';
              fontWeight = '700';
              if (isPast) {
                bg = theme.isDark ? 'rgba(255,255,255,0.08)' : '#c7d9f8';
                color = theme.isDark ? theme.textSecondary : '#3a5db0';
              } else {
                bg = theme.primary;
                color = theme.onPrimary;
              }
              if (isToday) {
                border = '2.5px solid #dc3545';
              }
            } else if (isToday) {
              bg = theme.isDark ? `rgba(255,255,255,0.08)` : `rgba(102,126,234,0.12)`;
              color = theme.primary;
              fontWeight = '700';
            }

            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'center', padding: '1px 0' }}>
                <div
                  onClick={hasGig ? () => setCalModal({ day, gigsOnDay }) : undefined}
                  style={{
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    fontSize: '0.78rem',
                    fontWeight,
                    background: bg,
                    color,
                    border,
                    cursor,
                    flexShrink: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  {day !== null ? day : ''}
                </div>
              </div>
            );
          })}
        </div>

        {/* Gig list for this month */}
        {sortedGigDays.length > 0 ? (
          <div style={{ marginTop: '0.65rem', paddingTop: '0.55rem', borderTop: `0.5px solid ${theme.border}`, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
            {sortedGigDays.map(day =>
              gigDateMap[day].map((gig, idx) => {
                const d = new Date(calYear, calMonth, day);
                const label = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                const isPast = day < calToday;
                return (
                  <div
                    key={`${day}-${idx}`}
                    onClick={() => setCalModal({ day, gigsOnDay: gigDateMap[day] })}
                    style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.8rem', cursor: 'pointer' }}
                  >
                    <span style={{ color: isPast ? theme.textMuted : theme.primary, fontWeight: '700', minWidth: '80px', flexShrink: 0 }}>{label}</span>
                    <span style={{ color: isPast ? theme.textMuted : theme.text }}>{gig.gig_name}</span>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <p style={{ margin: '0.55rem 0 0 0', fontSize: '0.73rem', color: theme.textMuted, textAlign: 'center' }}>
            No gigs scheduled this month
          </p>
        )}
      </div>
      {/* END LEFT column */}

      {/* RIGHT column: stats + nav cards */}
      <div>

      {/* Quick Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: isMobile ? '0.5rem' : '1rem',
        marginBottom: isMobile ? '1rem' : '1rem',
      }}>
        <div style={{
          background: theme.surfaceAlt,
          borderRadius: isMobile ? '8px' : '12px',
          padding: isMobile ? '0.75rem' : '1.5rem',
          textAlign: isMobile ? 'center' : 'left',
          border: `0.5px solid ${theme.border}`,
        }}>
          <p style={{ fontSize: isMobile ? '10px' : '13px', color: theme.textSecondary, margin: isMobile ? '0 0 0.25rem 0' : '0 0 0.5rem 0' }}>
            {isMobile ? 'Songs' : 'Active Songs'}
          </p>
          <p style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: '500', margin: 0, color: theme.text }}>
            {stats.totalSongs}
          </p>
        </div>

        <div style={{
          background: theme.surfaceAlt,
          borderRadius: isMobile ? '8px' : '12px',
          padding: isMobile ? '0.75rem' : '1.5rem',
          textAlign: isMobile ? 'center' : 'left',
          border: `0.5px solid ${theme.border}`,
        }}>
          <p style={{ fontSize: isMobile ? '10px' : '13px', color: theme.textSecondary, margin: isMobile ? '0 0 0.25rem 0' : '0 0 0.5rem 0' }}>
            {isMobile ? 'Gigs' : 'Total Gigs'}
          </p>
          <p style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: '500', margin: 0, color: theme.text }}>
            {stats.totalGigs}
          </p>
        </div>

        <div style={{
          background: theme.surfaceAlt,
          borderRadius: isMobile ? '8px' : '12px',
          padding: isMobile ? '0.75rem' : '1.5rem',
          textAlign: isMobile ? 'center' : 'left',
          border: `0.5px solid ${theme.border}`,
        }}>
          <p style={{ fontSize: isMobile ? '10px' : '13px', color: theme.textSecondary, margin: isMobile ? '0 0 0.25rem 0' : '0 0 0.5rem 0' }}>
            {isMobile ? 'Requests' : 'Pending Requests'}
          </p>
          <p style={{
            fontSize: isMobile ? '20px' : '28px', fontWeight: '500', margin: 0,
            color: stats.pendingRequests > 0 ? '#dc3545' : 'inherit',
          }}>
            {stats.pendingRequests}
          </p>
        </div>
      </div>

      {/* Navigation Cards — internal 2-col grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: isMobile ? '0.6rem' : '1.25rem',
      }}>
        {navCards.filter(c => canEdit || c.href !== '/dashboard/branding').map(({ href, Icon, title, desc }) => (
          <a
            key={href}
            href={href}
            style={{
              background: theme.surface,
              borderRadius: '12px',
              border: `0.5px solid ${theme.border}`,
              padding: isMobile ? '0.85rem 1rem' : '1.25rem 1.5rem',
              textDecoration: 'none',
              color: theme.text,
              boxShadow: theme.isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.08)',
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gridTemplateRows: 'auto auto',
              columnGap: '0.75rem',
              rowGap: '0.2rem',
              alignItems: 'center',
            }}
          >
            <span style={{ gridRow: '1', gridColumn: '1', display: 'flex', alignItems: 'center' }}><Icon size={28} color={theme.primary} /></span>
            <h3 style={{ margin: 0, fontSize: '1.05rem', gridRow: '1', gridColumn: '2', color: theme.text }}>{title}</h3>
            <p style={{ margin: 0, color: theme.textSecondary, fontSize: '0.85rem', gridRow: '2', gridColumn: '1 / 3' }}>{desc}</p>
          </a>
        ))}
      </div>

      </div>
      {/* END RIGHT column */}

      </div>
      {/* END 2-col grid */}

      {/* Gig detail modal */}
      {calModal && (
        <div
          onClick={() => setCalModal(null)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '1rem',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: theme.surface,
              borderRadius: '14px',
              padding: '1.5rem',
              maxWidth: '380px',
              width: '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
              maxHeight: '80vh',
              overflowY: 'auto',
              border: `0.5px solid ${theme.border}`,
            }}
          >
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: '700', color: theme.primary, letterSpacing: '0.07em' }}>
                {new Date(calYear, calMonth, calModal.day).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase()}
              </div>
              <button
                onClick={() => setCalModal(null)}
                style={{
                  background: 'none', border: 'none', fontSize: '1.3rem',
                  cursor: 'pointer', color: theme.textMuted, padding: '0 0 0 0.5rem', lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            {/* One card per gig on this day */}
            {calModal.gigsOnDay.map((gig, idx) => (
              <div
                key={gig.id}
                style={{
                  marginBottom: calModal.gigsOnDay.length > 1 && idx < calModal.gigsOnDay.length - 1 ? '1.25rem' : 0,
                  paddingBottom: calModal.gigsOnDay.length > 1 && idx < calModal.gigsOnDay.length - 1 ? '1.25rem' : 0,
                  borderBottom: calModal.gigsOnDay.length > 1 && idx < calModal.gigsOnDay.length - 1 ? `1px solid ${theme.border}` : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.65rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.15rem' }}>{gig.gig_name}</h3>
                  {gig.is_active && (
                    <span style={{ background: theme.primary, color: theme.onPrimary, fontSize: '0.65rem', fontWeight: '700', padding: '0.15rem 0.5rem', borderRadius: '10px' }}>
                      ACTIVE
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.88rem', color: theme.textSecondary, display: 'flex', gap: '0.4rem' }}>
                    <MapPin size={14} color={theme.textMuted} />
                    <span>{gig.venue_name}{gig.venue_location ? `, ${gig.venue_location}` : ''}</span>
                  </div>
                  {gig.gig_time && (
                    <div style={{ fontSize: '0.88rem', color: theme.textSecondary, display: 'flex', gap: '0.4rem' }}>
                      <Clock size={14} color={theme.textMuted} />
                      <span>{gig.gig_time.substring(0, 5)}</span>
                    </div>
                  )}
                  {(gig.number_of_sets || gig.set_duration_minutes) && (
                    <div style={{ fontSize: '0.88rem', color: theme.textSecondary, display: 'flex', gap: '0.4rem' }}>
                      <Music size={14} color={theme.textMuted} />
                      <span>
                        {gig.number_of_sets} set{gig.number_of_sets > 1 ? 's' : ''}
                        {gig.set_duration_minutes ? ` · ${gig.set_duration_minutes} mins each` : ''}
                      </span>
                    </div>
                  )}
                  {gig.notes && (
                    <div style={{ fontSize: '0.85rem', color: theme.textMuted, fontStyle: 'italic', marginTop: '0.15rem' }}>
                      {gig.notes}
                    </div>
                  )}
                </div>

                <a
                  href={`/dashboard/gigs?edit=${gig.id}`}
                  style={{
                    display: 'inline-block',
                    padding: '0.55rem 1.1rem',
                    background: theme.primary,
                    color: theme.onPrimary,
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '0.88rem',
                  }}
                >
                  Edit Gig
                </a>
              </div>
            ))}

            <button
              onClick={() => setCalModal(null)}
              style={{
                marginTop: '1rem',
                width: '100%',
                padding: '0.6rem',
                background: 'none',
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.88rem',
                color: theme.textSecondary,
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

export default Dashboard;
